import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Devices,
  Router,
  Wifi,
  CheckCircle,
  Warning,
  Block,
  Search,
  Clear,
  Map as MapIcon,
} from '@mui/icons-material';
import { ftthCaixaService } from '../../services/ftthCaixaService';
import equipmentService from '../../services/equipmentService';
import { contractService } from '../../services/contractService';
import circuitService, { Circuito } from '../../services/circuitService';
import EscritorioSelect from '../selects/EscritorioSelect';
import ContractServicosSelect from '../selects/ContractServicosSelect';
import EquipmentForm from '../forms/EquipmentForm';
import { fetchCaixaPorts } from '../../utils/loadCaixaPorts';
import { CaixaPort } from '../../types/caixaPort';
import CaixaMapComponent from '../../pages/ftth/components/CaixaMapComponent';

interface FtthCaixa {
  id: number;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  capacidade: number;
  disabled_ports: Array<{
    port: number;
    reason: string;
    date: string;
    usuarioId?: number;
  }> | Record<string, {
    reason: string;
    date: string;
    usuarioId?: number;
  }> | null;
  status: 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao';
}

type FtthCaixaPort = CaixaPort;

interface Equipment {
  id: number;
  modeloId: number;
  numero_serie: string;
  mac_address?: string;
  gpon_sn?: string;
  modalidade: 'proprio' | 'comodato';
  status_uso: 'livre' | 'em_uso';

  end_estoque?: string;
  modelo?: {
    id: number;
    titulo: string;
    tipo: 'ONU' | 'ONT' | 'Roteador' | 'Switch';
    marca?: {
      id: number;
      titulo: string;
    };
  };
}

interface AddOnuModalProps {
  open: boolean;
  onClose: () => void;
  onOnuAdded: () => void;
  contractId: number;
}

const AddOnuModal: React.FC<AddOnuModalProps> = ({
  open,
  onClose,
  onOnuAdded,
  contractId,
}) => {
  const [selectedEscritorioId, setSelectedEscritorioId] = useState<number | ''>('');
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [selectedCaixaId, setSelectedCaixaId] = useState<number | ''>('');
  const [caixaPorts, setCaixaPorts] = useState<FtthCaixaPort[]>([]);
  const [selectedPort, setSelectedPort] = useState<number | ''>('');
  const [circuitos, setCircuitos] = useState<Circuito[]>([]);
  const [selectedCircuitoOption, setSelectedCircuitoOption] = useState<Circuito | string | null>(null);
  const [availableEquipments, setAvailableEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [loadingCircuitos, setLoadingCircuitos] = useState(false);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [selectedServicoId, setSelectedServicoId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [caixaSearch, setCaixaSearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [caixaCoords, setCaixaCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // Limpar estados ao abrir o modal
  useEffect(() => {
    if (open) {
      // Debug: verificar usuário logado
      const authData = localStorage.getItem('authData');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          console.log('👤 Usuário logado:', parsed.user?.perfil, parsed.user?.nome); // Debug
        } catch (e) {
          console.warn('⚠️ Erro ao parse authData:', e);
        }
      }

      // Limpar estados quando abrir o modal
      setSelectedEscritorioId('');
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixas([]);
      setCaixaPorts([]);
      setCircuitos([]);
      setAvailableEquipments([]);
      setSelectedServicoId(null);
      setError(null);
      setCaixaSearch('');
      setEquipmentSearch('');
      setCaixaCoords(null);
      setMapModalOpen(false);
      loadContractData();
    }
  }, [open, contractId]);

  const loadContractData = async () => {
    try {
      const response = await contractService.getById(contractId);
      if (response.success && response.data) {
        setClientId(response.data.cliente_id);
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados do contrato:', err);
    }
  };

  // Carregar CAIXAs quando escritório for selecionado
  useEffect(() => {
    if (selectedEscritorioId) {
      // Limpar caixas anteriores e seleções ao trocar escritório
      setCaixas([]);
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixaPorts([]);
      setCircuitos([]);
      setAvailableEquipments([]);
      setCaixaSearch('');
      loadCaixas(selectedEscritorioId as number);
    } else {
      setCaixas([]);
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixaPorts([]);
      setCircuitos([]);
      setAvailableEquipments([]);
    }
  }, [selectedEscritorioId]);

  // Carregar portas quando CAIXA for selecionada
  useEffect(() => {
    if (selectedCaixaId) {
      loadCaixaPorts(selectedCaixaId as number);
      loadCircuitos(selectedCaixaId as number);
    } else {
      setCaixaPorts([]);
      setSelectedPort('');
      setCircuitos([]);
      setSelectedCircuitoOption(null);
    }
  }, [selectedCaixaId]);

  useEffect(() => {
    if (!selectedCaixaId) {
      setCaixaCoords(null);
      return;
    }

    const caixaFromList = caixas.find((caixa) => caixa.id === selectedCaixaId);
    if (caixaFromList?.latitude && caixaFromList?.longitude) {
      setCaixaCoords({
        latitude: Number(caixaFromList.latitude),
        longitude: Number(caixaFromList.longitude),
      });
      return;
    }

    let cancelled = false;
    const loadCaixaCoords = async () => {
      try {
        const response = await ftthCaixaService.getById(selectedCaixaId as number);
        if (cancelled) return;
        const caixa = response.data;
        if (caixa?.latitude && caixa?.longitude) {
          setCaixaCoords({
            latitude: Number(caixa.latitude),
            longitude: Number(caixa.longitude),
          });
        } else {
          setCaixaCoords(null);
        }
      } catch {
        if (!cancelled) setCaixaCoords(null);
      }
    };

    loadCaixaCoords();
    return () => {
      cancelled = true;
    };
  }, [selectedCaixaId, caixas]);

  const filteredCaixas = React.useMemo(() => {
    return caixas.filter(caixa =>
      caixa.name.toLowerCase().includes(caixaSearch.toLowerCase()) ||
      caixa.address.toLowerCase().includes(caixaSearch.toLowerCase())
    );
  }, [caixas, caixaSearch]);

  // Carregar equipamentos quando serviço, CAIXA e porta forem selecionados
  useEffect(() => {
    if (selectedServicoId && selectedCaixaId && selectedPort) {
      loadAvailableEquipments();
    } else {
      setAvailableEquipments([]);
    }
  }, [selectedServicoId, selectedCaixaId, selectedPort]);

  const loadCaixas = async (escritorioId: number) => {
    try {
      setLoadingCaixas(true);
      setError(null);

      console.log('🔍 Carregando CAIXAs do escritório:', escritorioId); // Debug

      // Buscar caixas filtradas pelo escritório
      const response = await ftthCaixaService.getCaixas({
        limit: 2000,
        tipo: 'cto',
        status: 'ativo',
        escritorio_id: escritorioId
      });

      let caixasData: FtthCaixa[] = [];

      // Verificar estrutura exata da resposta
      if (response && response.success && response.data && response.data.caixas) {
        caixasData = response.data.caixas;
      } else if (response && response.caixas) {
        caixasData = response.caixas;
      } else if (response && Array.isArray(response.data)) {
        caixasData = response.data;
      } else if (Array.isArray(response)) {
        caixasData = response;
      } else {
        console.error('❌ Estrutura de resposta inesperada da API CAIXAs');
        setError('Formato de dados inesperado da API');
        return;
      }

      console.log('✅ CAIXAs carregadas:', caixasData.length); // Debug

      if (caixasData.length === 0) {
        setCaixas([]); // Garantir que o array de caixas está vazio
        //setError('Nenhuma CAIXA encontrada neste escritório');
        return;
      }

      setCaixas(caixasData);
      setError(null); // Limpar erro quando caixas são carregadas com sucesso
    } catch (err: any) {
      console.error('❌ Erro ao carregar CAIXAs:', err);
      console.error('❌ Error details:', err.message, err.stack);
      setCaixas([]); // Limpar caixas em caso de erro
      setError(`Erro ao carregar CAIXAs: ${err.message}`);
    } finally {
      setLoadingCaixas(false);
    }
  };

  const loadCaixaPorts = async (caixaId: number) => {
    try {
      setLoadingPorts(true);
      setError(null);

      const ports = await fetchCaixaPorts(caixaId);
      setCaixaPorts(ports);
    } catch (err: any) {
      console.error('Erro ao carregar portas da CAIXA:', err);
      setError(err.message || 'Erro ao carregar portas da CAIXA');

      const selectedCaixa = caixas.find((caixa) => caixa.id === caixaId);
      if (selectedCaixa) {
        const basicPorts: FtthCaixaPort[] = [];
        const totalPorts = selectedCaixa.capacidade || 0;
        for (let i = 1; i <= totalPorts; i++) {
          basicPorts.push({ port_number: i, status: 'available' });
        }
        setCaixaPorts(basicPorts);
      }
    } finally {
      setLoadingPorts(false);
    }
  };

  const loadCircuitos = async (caixaId: number) => {
    try {
      setLoadingCircuitos(true);
      setError(null);

      console.log('🔌 Carregando circuitos da CAIXA:', caixaId);

      const response = await circuitService.getCircuitos({
        caixa_id: caixaId,
        situacao: 'livre',
        limit: 100
      });

      console.log('✅ Circuitos carregados:', response.circuitos.length);
      setCircuitos(response.circuitos || []);

    } catch (err: any) {
      console.error('❌ Erro ao carregar circuitos:', err);
      setError(`Erro ao carregar circuitos: ${err.message}`);
      setCircuitos([]);
    } finally {
      setLoadingCircuitos(false);
    }
  };

  const loadAvailableEquipments = async () => {
    try {
      setLoadingEquipments(true);
      setError(null);

      console.log('🔧 Carregando equipamentos disponíveis...'); // Debug

      const response = await equipmentService.getEquipments({
        status_uso: 'livre',
        limit: 100
      });

      console.log('✅ Resposta getEquipments:', response); // Debug
      console.log('✅ Type of response:', typeof response); // Debug
      console.log('✅ Response keys:', Object.keys(response || {})); // Debug

      // Log mais detalhado da estrutura
      if (response && response.data) {
        console.log('📊 response.data type:', typeof response.data);
        console.log('📊 response.data keys:', Object.keys(response.data || {}));
        console.log('📊 response.data.equipments exists:', !!(response.data as any).equipments);
        console.log('📊 response.data is array:', Array.isArray(response.data));
      }

      let equipmentsData: Equipment[] = [];

      // Verificar todas as estruturas possíveis de resposta
      if (response && response.success && response.data && (response.data as any).equipments && Array.isArray((response.data as any).equipments)) {
        // Estrutura: {success: true, data: {equipments: [...], pagination: {...}}}
        console.log('📁 Usando estrutura: response.data.equipments');
        equipmentsData = (response.data as any).equipments;
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        // Estrutura: {success: true, data: [...]}
        console.log('📁 Usando estrutura: response.data (array)');
        equipmentsData = response.data;
      } else if (response && (response as any).equipments && Array.isArray((response as any).equipments)) {
        // Estrutura: {equipments: [...], pagination: {...}}
        console.log('📁 Usando estrutura: response.equipments');
        equipmentsData = (response as any).equipments;
      } else if (response && Array.isArray(response)) {
        // Estrutura: [...] (array direto)
        console.log('📁 Usando estrutura: response (array direto)');
        equipmentsData = response;
      } else if (response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        // Verificar se data tem alguma propriedade que seja array
        console.log('🔍 Procurando arrays dentro de response.data...');
        const dataKeys = Object.keys(response.data);
        console.log('🔍 Data keys:', dataKeys);

        for (const key of dataKeys) {
          const value = (response.data as any)[key];
          if (Array.isArray(value)) {
            console.log(`📁 Encontrou array em response.data.${key}:`, value.length);
            equipmentsData = value;
            break;
          }
        }

        if (equipmentsData.length === 0) {
          console.error('❌ Nenhum array encontrado em response.data');
        }
      } else {
        console.error('❌ Estrutura de resposta inesperada:', response);
        console.error('❌ Response structure:', JSON.stringify(response, null, 2));
        setError('Formato de dados inesperado da API de equipamentos');
        return;
      }

      console.log('🎯 Equipamentos carregados:', equipmentsData.length, equipmentsData); // Debug

      if (equipmentsData.length === 0) {
        console.warn('⚠️ Nenhum equipamento encontrado');
        setAvailableEquipments([]);
        return;
      }

      // Filtrar apenas equipamentos ONU/ONT
      const onuOntEquipments = equipmentsData.filter((equipment: Equipment) => {
        const tipo = equipment.modelo?.tipo?.toLowerCase();
        return tipo === 'onu' || tipo === 'ont';
      });

      console.log('🔌 Equipamentos ONT/ONU filtrados:', onuOntEquipments.length, onuOntEquipments); // Debug
      setAvailableEquipments(onuOntEquipments);

    } catch (err: any) {
      console.error('❌ Erro ao carregar equipamentos:', err);
      console.error('❌ Error details:', err.message, err.stack);
      setError(`Erro ao carregar equipamentos: ${err.message}`);
    } finally {
      setLoadingEquipments(false);
    }
  };

  const filteredEquipments = React.useMemo(() => {
    return availableEquipments.filter(eq => {
      const search = equipmentSearch.toLowerCase();
      return (
        eq.numero_serie?.toLowerCase().includes(search) ||
        eq.gpon_sn?.toLowerCase().includes(search) ||
        eq.mac_address?.toLowerCase().includes(search) ||
        eq.modelo?.titulo?.toLowerCase().includes(search) ||
        eq.modelo?.marca?.titulo?.toLowerCase().includes(search) ||
        eq.end_estoque?.toLowerCase().includes(search)
      );
    });
  }, [availableEquipments, equipmentSearch]);

  const handleEquipmentSelect = async (equipment: Equipment) => {
    // Validar se serviço foi selecionado
    if (!selectedServicoId) {
      setError('Selecione um serviço antes de vincular o equipamento');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Preparar dados para atualização
      const updateData: any = {

        caixa_id: selectedCaixaId as number,
        caixa_port_number: selectedPort as number,
        contrato_id: contractId,
        contrato_servico_id: selectedServicoId
      };

      // Incluir circuito_id apenas se um circuito foi selecionado
      if (typeof selectedCircuitoOption === 'string') {

        // Criar o novo circuito
        const contractRes = await contractService.getById(contractId);
        let endereco = 'Endereço não informado';
        if (contractRes.success && contractRes.data?.endereco_inst) {
          endereco = contractRes.data.endereco_inst + ' - ' + contractRes.data.bairro_inst;
        }

        const newCircuito = await circuitService.createCircuit({
          identificacao: selectedCircuitoOption,
          caixa_id: selectedCaixaId as number,
          endereco: endereco,
          situacao: 'ocupado',
          contrato_id: contractId
        });
        updateData.circuito_id = newCircuito.circuit.id;
      } else if (selectedCircuitoOption && typeof selectedCircuitoOption === 'object' && 'id' in selectedCircuitoOption) {
        updateData.circuito_id = selectedCircuitoOption.id;
      }

      // Atualizar status de uso do equipamento
      await equipmentService.updateEquipment(equipment.id, updateData);

      onOnuAdded();
    } catch (err: any) {
      console.error('Erro ao vincular equipamento:', err);
      setError('Erro ao vincular equipamento ao contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentFormSuccess = () => {
    setEquipmentFormOpen(false);
    loadAvailableEquipments();
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    // Como estamos usando seleção única, esperamos apenas um valor
    if (Array.isArray(value)) {
      // Se por algum motivo retornar array, pegar o primeiro valor
      const firstValue = value[0];
      if (typeof firstValue === 'number') {
        setSelectedEscritorioId(firstValue);
      } else {
        setSelectedEscritorioId('');
      }
    } else {
      // Valor único (string ou number)
      if (typeof value === 'number') {
        setSelectedEscritorioId(value);
      } else {
        setSelectedEscritorioId('');
      }
    }
  };

  const handleServicoChange = (servicoId: number | null, servico?: any, contractId?: number) => {
    setSelectedServicoId(servicoId);
  };

  const handleClose = () => {
    setSelectedEscritorioId('');
    setSelectedCaixaId('');
    setSelectedPort('');
    setSelectedCircuitoOption(null);
    setCaixas([]);
    setCaixaPorts([]);
    setCircuitos([]);
    setAvailableEquipments([]);
    setSelectedServicoId(null);
    setCaixaSearch('');
    setEquipmentSearch('');
    setCaixaCoords(null);
    setMapModalOpen(false);
    setError(null);
    onClose();
  };

  const getPortLabel = (port: FtthCaixaPort) => {
    switch (port.status) {
      case 'available':
        return `Porta ${port.port_number} - Disponível`;
      case 'occupied':
        const clientName = port.info?.client_full_name || port.info?.client_name || 'Cliente';
        const contractTitle = port.info?.contract_title || `Contrato #${port.info?.contract_id}`;
        return `Porta ${port.port_number} - ${clientName} (${contractTitle})`;
      case 'disabled':
        return `Porta ${port.port_number} - Desabilitada (${port.info?.reason || 'Sem motivo'})`;
      default:
        return `Porta ${port.port_number}`;
    }
  };

  const getPortIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle color="success" />;
      case 'occupied':
        return <Warning color="warning" />;
      case 'disabled':
        return <Block color="error" />;
      default:
        return <Devices />;
    }
  };

  const getEquipmentTypeIcon = (tipo?: string) => {
    switch (tipo?.toLowerCase()) {
      case 'onu':
      case 'ont':
        return <Wifi />;
      case 'roteador':
        return <Router />;
      default:
        return <Devices />;
    }
  };

  const selectedCaixa = caixas.find(caixa => caixa.id === selectedCaixaId);

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Adicionar ONT/ONU ao Contrato
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 1.5 }} />}

        <Grid container pt={0.7} spacing={1.5}>
          {/* Seleção de Serviço */}
          <Grid item xs={12}>
            <ContractServicosSelect
              value={selectedServicoId}
              onChange={handleServicoChange}
              contractId={contractId}
              label="Serviço"
              placeholder="Selecione um serviço para vincular o equipamento"
              required
              disabled={loading}
              size="small"
              onlyActive={true}
              showContractTitle={false}
              allowClear={false}
              error={!!error && !selectedServicoId}
              helperText={!selectedServicoId ? 'Selecione um serviço para continuar' : ''}
            />
          </Grid>

          {/* Seleção de Cidade */}
          <Grid item xs={12} md={6}>
            <EscritorioSelect
              value={selectedEscritorioId === '' ? null : selectedEscritorioId}
              onChange={handleEscritorioChange}
              fullWidth
              size="small"
              required
              label="Escritório"
              placeholder="Selecione um escritório"
            />
          </Grid>

          {/* Seleção de CAIXA */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Selecione a CAIXA</InputLabel>
              <Select
                value={selectedCaixaId}
                onChange={(e) => setSelectedCaixaId(e.target.value as number)}
                label="Selecione a CAIXA"
                disabled={!selectedEscritorioId || loadingCaixas}
                size="small"
                MenuProps={{ autoFocus: false }}
              >
                {/* Campo de pesquisa dentro do Select */}
                <MenuItem
                  disableTouchRipple
                  sx={{
                    cursor: 'default',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'transparent' },
                    py: 1,
                    px: 1,
                    pointerEvents: 'auto'
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      e.stopPropagation();
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Pesquisar caixa..."
                    value={caixaSearch}
                    onChange={(e) => setCaixaSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Tab') {
                        e.stopPropagation();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: '1rem' }} />
                        </InputAdornment>
                      ),
                      endAdornment: caixaSearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCaixaSearch('');
                            }}
                          >
                            <Clear sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </MenuItem>

                {loadingCaixas ? (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: '0.8rem' }}>Carregando caixas...</Typography>
                  </MenuItem>
                ) : filteredCaixas.length === 0 && (selectedEscritorioId || caixaSearch) ? (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      {caixas.length === 0 ? 'Nenhuma caixa encontrada' : 'Nenhuma caixa corresponde à pesquisa'}
                    </Typography>
                  </MenuItem>
                ) : (
                  filteredCaixas.map((caixa) => (
                    <MenuItem key={caixa.id} value={caixa.id}>
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {caixa.name} - {caixa.address}
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Seleção de Porta */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!selectedCaixaId || loadingPorts} size="small">
              <InputLabel>Selecione a Porta</InputLabel>
              <Select
                value={selectedPort}
                onChange={(e) => setSelectedPort(e.target.value as number)}
                label="Selecione a Porta"
                size="small"
              >
                {caixaPorts.map((port) => (
                  <MenuItem
                    key={port.port_number}
                    value={port.port_number}
                    disabled={port.status !== 'available'}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getPortIcon(port.status)}
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {getPortLabel(port)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Seleção de Circuito */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={circuitos}
              getOptionLabel={(option) => typeof option === 'string' ? option : String((option as Circuito).identificacao)}
              disabled={!selectedCaixaId || loadingCircuitos}
              value={selectedCircuitoOption as any}
              onChange={(_, newValue) => setSelectedCircuitoOption(newValue)}
              onInputChange={(_, newInputValue) => {
                if (!circuitos.find(c => String(c.identificacao) === newInputValue)) {
                  setSelectedCircuitoOption(newInputValue);
                }
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                const isString = typeof option === 'string';
                const label = isString ? option : String((option as Circuito).identificacao);
                const end = !isString ? (option as Circuito).endereco : '';
                return (
                  <li {...optionProps} key={key || (isString ? option : (option as Circuito).id)}>
                    <Box>
                      <Typography variant="body2">{label}</Typography>
                      {end && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                          {end}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Circuito (Opcional)"
                  placeholder={loadingCircuitos ? "Carregando..." : "Selecione um na lista ou digite um novo..."}
                />
              )}
            />
          </Grid>

          {/* Informações da CAIXA Selecionada */}
          {selectedCaixa && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      Informações da CAIXA Selecionada
                    </Typography>
                    {caixaCoords && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setMapModalOpen(true)}
                        sx={{ fontSize: '0.75rem', py: 0.25, px: 1, minHeight: 28 }}
                      >
                        Ver no mapa
                      </Button>
                    )}
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Nome: <strong>{selectedCaixa.name}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Total de Portas: <strong>{selectedCaixa.capacidade}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Endereço: <strong>{selectedCaixa.address}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Tabela de Equipamentos Disponíveis */}
          {selectedServicoId && selectedCaixaId && selectedPort && (
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                mb={1}
                gap={1}
              >
                <Box
                  display="flex"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={{ xs: 1, sm: 2 }}
                  flex={1}
                >
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                    Equipamentos Disponíveis
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Pesquisar por série, MAC, GPON..."
                    value={equipmentSearch}
                    onChange={(e) => setEquipmentSearch(e.target.value)}
                    sx={{
                      width: '100%',
                      maxWidth: { xs: '100%', sm: 350 },
                      flex: { sm: 1 },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: '1rem' }} />
                        </InputAdornment>
                      ),
                      endAdornment: equipmentSearch && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setEquipmentSearch('')}>
                            <Clear sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setEquipmentFormOpen(true)}
                  disabled={loadingEquipments}
                  size="small"
                  sx={{
                    fontSize: '0.8rem',
                    px: 1.5,
                    py: 0.75,
                    width: { xs: '100%', sm: 'auto' },
                    height: { sm: 32 },
                  }}
                >
                  Cadastrar Novo
                </Button>
              </Box>
              <EquipmentForm
                open={equipmentFormOpen}
                onClose={() => setEquipmentFormOpen(false)}
                onSuccess={handleEquipmentFormSuccess}
              />
              {loadingEquipments ? (
                <LinearProgress />
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>Tipo</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>Marca/Modelo</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>N.Série</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>GPON / MAC</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>Mod.</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.8rem', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', fontWeight: 700 }}>Estoque</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEquipments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <Box sx={{ opacity: 0.5, textAlign: 'center' }}>
                              <Router sx={{ fontSize: 40, mb: 1 }} />
                              <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                {availableEquipments.length === 0
                                  ? 'Nenhum equipamento disponível'
                                  : 'Nenhum equipamento encontrado para a pesquisa'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEquipments.map((equipment) => (
                          <TableRow
                            key={equipment.id}
                            hover
                            sx={{ cursor: selectedServicoId ? 'pointer' : 'not-allowed', opacity: selectedServicoId ? 1 : 0.5 }}
                            onClick={() => selectedServicoId && handleEquipmentSelect(equipment)}
                          >
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                                  {getEquipmentTypeIcon(equipment.modelo?.tipo)}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {equipment.modelo?.tipo || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {equipment.modelo?.marca?.titulo || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {equipment.modelo?.titulo || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                                {equipment.numero_serie}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                                {equipment.gpon_sn || '-'}
                              </Typography>

                              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                                {equipment.mac_address || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Chip
                                label={equipment.modalidade === 'proprio' ? 'Próprio' : 'Comodato'}
                                color={equipment.modalidade === 'proprio' ? 'primary' : 'secondary'}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1.5 }}>
                              <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                                {equipment.end_estoque || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>

    {caixaCoords && selectedCaixa && (
      <Dialog
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Localização — {selectedCaixa.name}
          </Typography>
          <IconButton onClick={() => setMapModalOpen(false)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2, pt: 0 }}>
          <CaixaMapComponent
            latitude={caixaCoords.latitude}
            longitude={caixaCoords.longitude}
            caixaName={selectedCaixa.name}
            address={selectedCaixa.address}
            height={380}
          />
          <Box display="flex" justifyContent="center" mt={1}>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {caixaCoords.latitude.toFixed(6)}, {caixaCoords.longitude.toFixed(6)}
              {' • '}
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const url = `https://www.google.com/maps?q=${caixaCoords.latitude},${caixaCoords.longitude}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontSize: 'inherit', verticalAlign: 'baseline' }}
              >
                Abrir no Google Maps
              </Button>
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default AddOnuModal;
